/**
 * Quale punteggio si sta guardando: variante + cadenza.
 *
 * I rating sono otto per variante (quattro cadenze × valida/amichevole non
 * esiste: la cadenza è l'unico asse oltre alla variante), e fino a ieri la lista
 * dei collegati ne mostrava SEMPRE uno solo — il blitz internazionale messo in
 * cache al collegamento — anche a chi stava scegliendo un avversario di dama
 * italiana a cadenza rapid. Questo modulo decide di quale colonna si parla.
 *
 * La cadenza viaggia come CATEGORIA (`blitz`), non come preset (`blitz-5+3`):
 * `blitz-5+3` e `blitz-3+2` leggono la stessa colonna, e nella Sfida diretta i
 * menu di minuti e incremento cambiano a ogni click. Con la categoria quei
 * click non generano nemmeno una richiesta.
 */

import { VALID_VARIANT_STRINGS } from '@draughts/shared';
import { parseTimeControl } from './cadence-icons';

/** Le quattro categorie che hanno una colonna di rating. `none` (∞) non c'è. */
export type RatingCadence = 'bullet' | 'blitz' | 'rapid' | 'classical';

const RATING_CADENCES: readonly RatingCadence[] = ['bullet', 'blitz', 'rapid', 'classical'];

export interface RatingBucket {
  /** Variante in minuscolo, come la vuole il server (`italian`, `international`…). */
  variant: string;
  cadence: RatingCadence;
}

/** Chiave stabile per confronti e dipendenze di effetto: `italian:blitz`. */
export function ratingBucketKey(bucket: RatingBucket): string {
  return `${bucket.variant}:${bucket.cadence}`;
}

/**
 * Bucket da variante + controllo di tempo di un modulo, con ripiego.
 *
 * Il ripiego serve davvero: `'none'` (partita senza orologio) non ha nessuna
 * colonna di rating, e il modulo della Sfida diretta parte con `settings` a
 * `null` finché i campi non si annunciano. In entrambi i casi si mostra il
 * punteggio nelle preferenze di chi guarda — e il chip in testata lo dichiara,
 * quindi il numero non resta mai senza etichetta.
 */
export function ratingBucketFrom(
  variant: string | null | undefined,
  timeControl: string | null | undefined,
  fallback: Partial<RatingBucket> | null | undefined,
): RatingBucket {
  /* Anche il ripiego si controlla: arriva dalle preferenze, che durante
     l'idratazione possono non esserci ancora. Un bucket con una cadenza
     `undefined` il server lo rifiuta, e il chip sparirebbe senza dire perche'. */
  const ripiegoVariante = String(fallback?.variant ?? '').toLowerCase();
  const ripiego: RatingBucket = {
    variant: VALID_VARIANT_STRINGS.includes(ripiegoVariante) ? ripiegoVariante : 'international',
    cadence: RATING_CADENCES.includes(fallback?.cadence as RatingCadence)
      ? (fallback?.cadence as RatingCadence)
      : 'blitz',
  };

  const v = String(variant ?? '').toLowerCase();
  const categoria = parseTimeControl(timeControl ?? null)?.category;
  return {
    variant: VALID_VARIANT_STRINGS.includes(v) ? v : ripiego.variant,
    cadence: RATING_CADENCES.includes(categoria as RatingCadence)
      ? (categoria as RatingCadence)
      : ripiego.cadence,
  };
}
